import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useAnalytics } from 'src/components/settings/analytics';
import { useIntl } from 'src/hooks/intl';
import {
  CompareBranchesOptions,
  closeCompareBranchesDialog,
  compareBranches,
  getCompareBranchesDialogSlice,
} from 'src/redux/branches';
import { getCurrentPullRequestId } from 'src/redux/pull-request/selectors';
import { StrategySelect } from 'src/sections/repository/components/strategy-select';
import { getBranchDialogHeight } from 'src/sections/repository/sections/branches/components/dialogs/utils';
import { REPO_EVENT_SOURCES } from 'src/sections/repository/types';
import {
  SyncStrategyOption,
  getStrategyOption,
} from 'src/sections/repository/utils/merge-strategies';
import { getIsSiteMessageActive } from 'src/selectors/global-selectors';
import { SyncStrategy } from 'src/types/pull-request';
import { getBranchesFullNames } from 'src/utils/compare-branches';

import BranchDialog from './branch-dialog';
import messages from './sync-branch-dialog.i18n';

type SyncBranchDialogProps = {
  syncStrategies?: SyncStrategy[];
  analyticsParentPage?: string;
};

export const SyncBranchDialog = ({
  syncStrategies,
  analyticsParentPage,
}: SyncBranchDialogProps): JSX.Element => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const {
    isOpen,
    isLoading,
    isMerge,
    sourceBranchName,
    destinationBranchName,
    sourceRepositoryFullName,
    destinationRepositoryFullName,
  } = useSelector(getCompareBranchesDialogSlice);
  const currentPullRequestId = useSelector(getCurrentPullRequestId);
  const isBannerOpen = useSelector(getIsSiteMessageActive);
  const analyticsSource = REPO_EVENT_SOURCES.BRANCH_SYNC_MODAL;
  const { publishScreenEvent } = useAnalytics({ source: analyticsSource });
  const [initialLoad, setInitialLoad] = useState(true);

  const getDefaultSyncStrategy = useCallback(
    (strategies: SyncStrategy[] | undefined): SyncStrategy | undefined => {
      if (!strategies) {
        return undefined;
      } else if (strategies.includes(SyncStrategy.MergeCommit)) {
        return SyncStrategy.MergeCommit;
      } else {
        return strategies[0];
      }
    },
    []
  );

  const [selectedSyncStrategy, setSelectedSyncStrategy] = useState<
    SyncStrategy | undefined
  >(getDefaultSyncStrategy(syncStrategies));

  const syncStrategyOption = useMemo(
    () =>
      selectedSyncStrategy
        ? getStrategyOption(selectedSyncStrategy, intl)
        : undefined,
    [selectedSyncStrategy, intl]
  );

  useEffect(() => {
    if (syncStrategies) {
      setSelectedSyncStrategy(getDefaultSyncStrategy(syncStrategies));
    }
  }, [setSelectedSyncStrategy, getDefaultSyncStrategy, syncStrategies]);

  const isSyncBranchDialogOpen = isOpen && !isMerge;
  const onClose = () => dispatch(closeCompareBranchesDialog());
  const onAction = useCallback(
    (payload: CompareBranchesOptions) => {
      dispatch(compareBranches({ ...payload, strategy: selectedSyncStrategy }));
    },
    [dispatch, selectedSyncStrategy]
  );

  const getCommitMessage = () => {
    if (
      selectedSyncStrategy !== SyncStrategy.Rebase &&
      sourceBranchName &&
      destinationBranchName
    ) {
      const fullNames = getBranchesFullNames(
        sourceBranchName,
        destinationBranchName,
        sourceRepositoryFullName,
        destinationRepositoryFullName
      );

      return intl.formatMessage(messages.commitMessage, fullNames);
    }
    return undefined;
  };

  useEffect(() => {
    if (isSyncBranchDialogOpen && analyticsParentPage && initialLoad) {
      publishScreenEvent({
        attributes: {
          namespaces: [analyticsParentPage, analyticsSource],
        },
      });
      setInitialLoad(false);
    }
  }, [
    isSyncBranchDialogOpen,
    publishScreenEvent,
    analyticsParentPage,
    analyticsSource,
    initialLoad,
    setInitialLoad,
  ]);

  return (
    <BranchDialog
      isOpen={isSyncBranchDialogOpen}
      isLoading={isLoading}
      sourceBranchName={sourceBranchName || undefined}
      destinationBranchName={destinationBranchName || undefined}
      sourceRepositoryFullName={sourceRepositoryFullName || undefined}
      destinationRepositoryFullName={destinationRepositoryFullName || undefined}
      currentPullRequestId={currentPullRequestId || undefined}
      height={getBranchDialogHeight(syncStrategies, selectedSyncStrategy)}
      title={messages.dialogTitle}
      actionName={messages.actionName}
      commitMessage={getCommitMessage()}
      isCommitMessageDivRemoved={selectedSyncStrategy === SyncStrategy.Rebase}
      childrenAboveCommitMsgDiv
      closeSourceBranchEnabled={false}
      onClose={onClose}
      onAction={onAction}
    >
      {syncStrategies && syncStrategies.length > 1 && (
        <StrategySelect
          isBannerOpen={isBannerOpen}
          label="Sync strategy"
          strategies={syncStrategies}
          value={syncStrategyOption}
          onChange={(option: SyncStrategyOption) =>
            setSelectedSyncStrategy(option.value)
          }
        />
      )}
    </BranchDialog>
  );
};
